<template>
    <!--suppress VueMissingComponentImportInspection -->
    <notifications
        position="top center"
        :duration="-1"
        :max="3"
        reverse
    >
        <!--suppress VueUnrecognizedSlot -->
        <template #body="{ item, close }">
            <div class="vue-notification-template" :class="['notification-' + (item.type || 'default')]">
                <div class="notification-close" @click.stop="close">
                    <img v-svg-inline src="@/assets/icons/ui/close.svg" />
                </div>

                <div class="notification-icon">
                    <img
                        v-if="item.type === 'success'"
                        v-svg-inline
                        src="@/assets/icons/ui/ni-success.svg"
                        :alt="item.type"
                    />

                    <img
                        v-if="item.type === 'error'"
                        v-svg-inline
                        src="@/assets/icons/ui/ni-error.svg"
                        :alt="item.type"
                    />

                    <img
                        v-if="item.type === 'warn'"
                        v-svg-inline
                        src="@/assets/icons/ui/ni-warn.svg"
                        :alt="item.type"
                    />
                </div>

                <div class="notification-content">
                    <div class="notification-title">
                        {{ item.title }}
                    </div>

                    <div v-if="item.data.subtitle" class="notification-subtitle">
                        {{ item.data.subtitle }}
                    </div>

                    <div v-if="item.text" class="notification-content__text">
                        {{ item.text }}
                    </div>

                    <div v-if="item.data.errors" class="notification-content__errors">
                        <div
                            v-for="(error, title) in getErrors(item)"
                            class="notification-content__error"
                        >
                            <div v-if="!Array.isArray(item.data.errors)" class="notification-content__error-title">
                                {{ title }}:
                            </div>

                            <div class="notification-content__error-text">
                                {{ error }}
                            </div>
                        </div>

                        <div
                            v-if="getMoreErrorsMessage(item)"
                            class="notification-content__more-errors"
                        >
                            {{ getMoreErrorsMessage(item) }}
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </notifications>
</template>

<script lang="ts" setup>
    import { NotificationItem } from '@kyvg/vue3-notification';
    import { choice } from '@/utils/NumberFormatter.ts';

    type ProlongedNotificationItem = NotificationItem & {
        data?: {
            subtitle?: string,
            errors?: string[] | Record<string, string>,
            maxErrors?: number,
        },
    };

    function getErrors(item: ProlongedNotificationItem): string[] | Record<string, string> {
        const errors: string[] | Record<string, string> = item.data?.errors ?? [];
        const maxErrors = item.data?.maxErrors;

        if (Array.isArray(errors)) {
            return maxErrors && errors.length > maxErrors
                ? errors.slice(0, maxErrors - 1)
                : [...errors];
        }

        if (!maxErrors || Object.keys(errors).length <= maxErrors) {
            return errors;
        }

        const slicedErrors: Record<string, string> = {};

        Object.keys(errors).slice(0, maxErrors - 1).forEach((title) => {
            slicedErrors[title] = errors[title];
        });

        return slicedErrors;
    }

    function getMoreErrorsMessage(item: ProlongedNotificationItem): string | undefined {
        const errors: string[] | Record<string, string> = item.data?.errors ?? [];
        const maxErrors = item.data?.maxErrors;
        const errorsCount = Array.isArray(errors) ? errors.length : Object.keys(errors).length;

        if (maxErrors && errorsCount > maxErrors) {
            const moreErrorsCount = errorsCount - maxErrors + 1;

            return `И ещё ${moreErrorsCount} ${choice(moreErrorsCount, 'ошибка', 'ошибки', 'ошибок')}`;
        }
    }
</script>

<style scoped lang="scss">
    @use "NotificationsView";
</style>
